<template>
  <div class="border rounded p-1 custom-editor">
    <div v-if="editor" class="toolbar d-flex flex-wrap mb-1">

      <!-- Bold -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('bold'), disabled: !editor.can().chain().focus().toggleBold().run() }"
          @click="editor.chain().focus().toggleBold().run()"
          title="Bold (Ctrl+B)"
      >
        <i class="fas fa-bold"></i>
      </span>

      <!-- Italic -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('italic'), disabled: !editor.can().chain().focus().toggleItalic().run() }"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Italic (Ctrl+I)"
      >
        <i class="fas fa-italic"></i>
      </span>

      <!-- Strikethrough -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('strike'), disabled: !editor.can().chain().focus().toggleStrike().run() }"
          @click="editor.chain().focus().toggleStrike().run()"
          title="Strikethrough"
      >
        <i class="fas fa-strikethrough"></i>
      </span>

      <!-- Inline Code -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('code'), disabled: !editor.can().chain().focus().toggleCode().run() }"
          @click="editor.chain().focus().toggleCode().run()"
          title="Inline Code"
      >
        <i class="fas fa-code"></i>
      </span>

      <!-- Clear Marks -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().unsetAllMarks().run()"
          title="Clear Marks"
      >
        <i class="fas fa-eraser"></i>
      </span>

      <!-- Clear Nodes -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().clearNodes().run()"
          title="Clear Nodes"
      >
        <i class="fas fa-trash-alt"></i>
      </span>

      <!-- Paragraph -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('paragraph'), disabled: !editor.can().chain().focus().setParagraph().run() }"
          @click="editor.chain().focus().setParagraph().run()"
          title="Paragraph"
      >
        <i class="fas fa-paragraph"></i>
      </span>

      <!-- Headings -->
      <span
          v-for="level in [1, 2, 3, 4, 5, 6]"
          :key="level"
          class="toolbar-icon"
          :class="{ active: editor.isActive('heading', { level }) }"
          @click="editor.chain().focus().toggleHeading({ level }).run()"
          :title="`Heading ${level}`"
      >
        H{{ level }}
      </span>

      <!-- Bullet List -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Bullet List"
      >
        <i class="fas fa-list-ul"></i>
      </span>

      <!-- Ordered List -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Ordered List"
      >
        <i class="fas fa-list-ol"></i>
      </span>

      <!-- Code Block -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('codeBlock') }"
          @click="editor.chain().focus().toggleCodeBlock().run()"
          title="Code Block"
      >
        <i class="fas fa-file-code"></i>
      </span>

      <!-- Blockquote -->
      <span
          class="toolbar-icon"
          :class="{ active: editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
          title="Blockquote"
      >
        <i class="fas fa-quote-right"></i>
      </span>

      <!-- Horizontal Rule -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().setHorizontalRule().run()"
          title="Horizontal Rule"
      >
        <i class="fas fa-minus"></i>
      </span>

      <!-- Hard Break -->
      <span
          class="toolbar-icon"
          @click="editor.chain().focus().setHardBreak().run()"
          title="Hard Break"
      >
        <i class="fas fa-level-down-alt"></i>
      </span>

      <!-- Undo -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().chain().focus().undo().run() }"
          @click="editor.chain().focus().undo().run()"
          title="Undo (Ctrl+Z)"
      >
        <i class="fas fa-undo"></i>
      </span>

      <!-- Redo -->
      <span
          class="toolbar-icon"
          :class="{ disabled: !editor.can().chain().focus().redo().run() }"
          @click="editor.chain().focus().redo().run()"
          title="Redo (Ctrl+Y)"
      >
        <i class="fas fa-redo"></i>
      </span>
    </div>
    <hr />

    <EditorContent :editor="editor" class="border rounded" />
  </div>
</template>

<script setup>
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import {
  EditorContent,
  useEditor,
} from '@tiptap/vue-3'
import {onBeforeUnmount, watch} from "vue";

const props  = defineProps({
  content: {
    type:String,
    default: ''
  },
});

const editor = useEditor({
  content: props.content,
  extensions: [
    StarterKit,
    TextAlign.configure({
      types: [
        'heading',
        'paragraph',
      ],
    }),
    Placeholder.configure({ placeholder: 'Write something here...' }),
    Underline,
  ],
    onUpdate({ editor }) {
     emit('update:content', editor.getHTML())
  }
})

const emit = defineEmits(['update:content'])

watch(
    () => props.content,
    (newContent) => {
      if (editor.value && newContent !== editor.value.getHTML()) {
        editor.value.commands.setContent(newContent)
      }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
  editor.value ? editor.value.destroy() : ''
})

defineExpose({
  getHTML: () => editor.value ? editor.value.getHTML() : '',
})
</script>

<style>
.custom-editor .ProseMirror {
  min-height: 100px;
  outline: none;
  overflow-y: auto;
  padding: 0.5rem;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1rem;
}

.toolbar-icon {
  cursor: pointer;
  font-size: 1.2rem;
  color: #444;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  transition: color 0.2s ease;
  border-radius: 4px;
}

.toolbar-icon:hover {
  color: #1976d2;
  background-color: rgba(25, 118, 210, 0.1);
}

.toolbar-icon.active {
  color: white;
  background-color: #1976d2;
}

.toolbar-icon.disabled {
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}
</style>